import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useLanguage } from '../../contexts/LanguageContext';
import { BranchTransfer } from '../../types/branchTransfers.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';

interface BranchTransferPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  transfer: BranchTransfer | null;
  onEdit?: () => void;
}

const BranchTransferPreviewDialog: React.FC<BranchTransferPreviewDialogProps> = ({
  open,
  onClose,
  transfer,
  onEdit
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    if (open) {
      companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
    }
  }, [open]);

  if (!transfer) return null;

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'Draft': return isRTL ? 'مسودة' : 'Draft';
      case 'Approved': return isRTL ? 'معتمد' : 'Approved';
      case 'Received': return isRTL ? 'مستلم' : 'Received';
      case 'Posted': return isRTL ? 'مرحّل' : 'Posted';
      default: return status || '-';
    }
  };

  const getStatusColor = (status?: string): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' => {
    switch (status) {
      case 'Draft': return 'default';
      case 'Approved': return 'warning';
      case 'Received': return 'info';
      case 'Posted': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatNumber = (num?: number) => (num ?? 0).toFixed(3);

  const canEdit = transfer.status === 'Draft';

  const handlePrint = () => {

    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:#1976d2;margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const docTitle = isRTL ? 'تحويل بين فروع' : 'Branch Transfer';

    const detailRows = transfer.details && transfer.details.length > 0
      ? transfer.details.map((d, i) => `
        <tr>
          <td style="text-align:center;">${i + 1}</td>
          <td><strong>${d.itemCode}</strong><br/><span style="color:#666;font-size:11px;">${isRTL ? d.itemNameAr : d.itemName}</span></td>
          <td>${isRTL ? d.unitNameAr : d.unitName}</td>
          <td style="text-align:center;font-weight:bold;">${formatNumber(d.quantity)}</td>
          <td style="text-align:center;">${formatNumber(d.unitCost)}</td>
          <td style="text-align:center;font-weight:bold;">${formatNumber(d.quantity * d.unitCost)}</td>
          <td>${d.notes || '-'}</td>
        </tr>
      `).join('')
      : `<tr><td colspan="7" style="text-align:center;color:#999;">${isRTL ? 'لا توجد تفاصيل' : 'No details'}</td></tr>`;

    const totalQty = transfer.details?.reduce((s, d) => s + d.quantity, 0) || 0;
    const totalCost = transfer.details?.reduce((s, d) => s + d.quantity * d.unitCost, 0) || 0;

    const html = `
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${docTitle} - ${transfer.transferNumber}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; font-size: 13px; color: #333; direction: ${isRTL ? 'rtl' : 'ltr'}; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px 10px; font-size: 12px; }
          th { background: #1976d2; color: white; font-weight: bold; }
          tr:nth-child(even) { background: #f9f9f9; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; }
          .info-item label { font-size: 11px; color: #999; display: block; }
          .info-item span { font-weight: bold; font-size: 14px; }
          .branch-box { padding: 12px; border-radius: 6px; text-align: center; }
          .branch-from { background: #ffebee; border: 1px solid #ef9a9a; }
          .branch-to { background: #e8f5e9; border: 1px solid #a5d6a7; }
          .branch-grid { display: grid; grid-template-columns: 1fr auto 1fr; gap: 10px; align-items: center; margin: 15px 0; }
          .arrow { font-size: 30px; color: #1976d2; text-align: center; }
          .status { display: inline-block; padding: 3px 12px; border-radius: 12px; font-size: 11px; font-weight: bold; color: white; }
          .status-Draft { background: #9e9e9e; }
          .status-Approved { background: #ed6c02; }
          .status-Received { background: #0288d1; }
          .status-Posted { background: #2e7d32; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
          .sig-box { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #000; padding-top: 5px; margin-top: 60px; }
          @page { size: A4; margin: 1cm; }
        </style>
      </head>
      <body>
        ${companyHeader}
        <h1 style="text-align:center;color:#1976d2;margin:10px 0 20px;">${docTitle}</h1>

        <div style="border:2px solid #1976d2;border-radius:8px;padding:15px;margin-bottom:20px;">
          <div class="info-grid">
            <div class="info-item">
              <label>${isRTL ? 'رقم التحويل' : 'Transfer Number'}</label>
              <span style="color:#1976d2;">${transfer.transferNumber}</span>
            </div>
            <div class="info-item">
              <label>${isRTL ? 'التاريخ' : 'Date'}</label>
              <span>${formatDate(transfer.transferDate)}</span>
            </div>
            <div class="info-item">
              <label>${isRTL ? 'الحالة' : 'Status'}</label>
              <span class="status status-${transfer.status}">${getStatusLabel(transfer.status)}</span>
            </div>
            ${transfer.journalEntryId ? `
            <div class="info-item">
              <label>${isRTL ? 'رقم القيد' : 'Journal Entry'}</label>
              <span style="color:#2e7d32;">${transfer.journalEntryNumber || ''} (#${transfer.journalEntryId})</span>
            </div>` : ''}
          </div>

          <hr style="border:none;border-top:1px solid #ddd;margin:10px 0;" />

          <div class="branch-grid">
            <div class="branch-box branch-from">
              <div style="font-size:11px;color:#999;">${isRTL ? 'من الفرع' : 'From Branch'}</div>
              <div style="font-weight:bold;color:#d32f2f;font-size:15px;">${isRTL ? (transfer.fromBranchNameAr || transfer.fromBranchName) : transfer.fromBranchName}</div>
              <div style="font-size:11px;color:#999;">${transfer.fromBranchCode || ''}</div>
            </div>
            <div class="arrow">⟶</div>
            <div class="branch-box branch-to">
              <div style="font-size:11px;color:#999;">${isRTL ? 'إلى الفرع' : 'To Branch'}</div>
              <div style="font-weight:bold;color:#2e7d32;font-size:15px;">${isRTL ? (transfer.toBranchNameAr || transfer.toBranchName) : transfer.toBranchName}</div>
              <div style="font-size:11px;color:#999;">${transfer.toBranchCode || ''}</div>
            </div>
          </div>

          ${transfer.notes ? `<div style="margin-top:10px;"><label style="font-size:11px;color:#999;">${isRTL ? 'ملاحظات:' : 'Notes:'}</label><p>${transfer.notes}</p></div>` : ''}
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>${isRTL ? 'الصنف' : 'Item'}</th>
              <th>${isRTL ? 'الوحدة' : 'Unit'}</th>
              <th>${isRTL ? 'الكمية' : 'Quantity'}</th>
              <th>${isRTL ? 'تكلفة الوحدة' : 'Unit Cost'}</th>
              <th>${isRTL ? 'الإجمالي' : 'Total'}</th>
              <th>${isRTL ? 'ملاحظات' : 'Notes'}</th>
            </tr>
          </thead>
          <tbody>
            ${detailRows}
            <tr style="background:#e3f2fd;font-weight:bold;">
              <td colspan="3" style="text-align:${isRTL ? 'left' : 'right'};">${isRTL ? 'الإجمالي:' : 'Total:'}</td>
              <td style="text-align:center;">${formatNumber(totalQty)}</td>
              <td></td>
              <td style="text-align:center;">${formatNumber(totalCost)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <div class="signatures">
          <div class="sig-box"><div class="sig-line">${isRTL ? 'مدير الفرع (المرسل)' : 'Branch Manager (Sender)'}</div></div>
          <div class="sig-box"><div class="sig-line">${isRTL ? 'المستلم' : 'Receiver'}</div></div>
          <div class="sig-box"><div class="sig-line">${isRTL ? 'مدير الفرع (المستقبل)' : 'Branch Manager (Receiver)'}</div></div>
        </div>
      </body>
      </html>
    `;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 500);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{ sx: { borderRadius: 3, bgcolor: isDarkMode ? '#1a1a2e' : 'white' } }}
    >
      <DialogTitle
        sx={{
          background: isDarkMode
            ? 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)'
            : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHorizIcon />
          <Box component="span">{isRTL ? 'معاينة تحويل الفروع' : 'Branch Transfer Preview'}</Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {/* Header Info */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'رقم التحويل' : 'Transfer Number'}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">{transfer.transferNumber}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'التاريخ' : 'Date'}
              </Typography>
              <Typography variant="body1" fontWeight="bold">{formatDate(transfer.transferDate)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'الحالة' : 'Status'}
              </Typography>
              <Box><Chip label={getStatusLabel(transfer.status)} color={getStatusColor(transfer.status)} size="small" sx={{ fontWeight: 'bold' }} /></Box>
            </Box>
            {transfer.journalEntryId && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'رقم القيد' : 'Journal Entry'}
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">{transfer.journalEntryNumber || ''} (#{transfer.journalEntryId})</Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Branches */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 2, alignItems: 'center' }}>
            <Box sx={{ p: 2, bgcolor: isDarkMode ? 'rgba(244,67,54,0.1)' : '#ffebee', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'من الفرع' : 'From Branch'}
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="error.main">
                {isRTL ? (transfer.fromBranchNameAr || transfer.fromBranchName) : transfer.fromBranchName}
              </Typography>
              <Typography variant="caption" color="text.secondary">{transfer.fromBranchCode}</Typography>
            </Box>
            <SwapHorizIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box sx={{ p: 2, bgcolor: isDarkMode ? 'rgba(76,175,80,0.1)' : '#e8f5e9', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'إلى الفرع' : 'To Branch'}
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="success.main">
                {isRTL ? (transfer.toBranchNameAr || transfer.toBranchName) : transfer.toBranchName}
              </Typography>
              <Typography variant="caption" color="text.secondary">{transfer.toBranchCode}</Typography>
            </Box>
          </Box>

          {transfer.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                {isRTL ? 'ملاحظات:' : 'Notes:'}
              </Typography>
              <Typography variant="body2">{transfer.notes}</Typography>
            </Box>
          )}
        </Paper>

        {/* Items Details */}
        <Paper elevation={2} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: isDarkMode ? 'rgba(25,118,210,0.3)' : '#1976d2', color: 'white', p: 1.5, fontWeight: 'bold' }}>
            {isRTL ? '📦 تفاصيل الأصناف' : '📦 Items Details'}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الكمية' : 'Quantity'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'تكلفة الوحدة' : 'Unit Cost'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الإجمالي' : 'Total'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'ملاحظات' : 'Notes'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transfer.details && transfer.details.length > 0 ? (
                  transfer.details.map((d, idx) => (
                    <TableRow key={d.branchTransferDetailId || idx} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        <strong>{d.itemCode}</strong><br />
                        <Typography variant="caption" color="text.secondary">{isRTL ? d.itemNameAr : d.itemName}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{isRTL ? d.unitNameAr : d.unitName}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{formatNumber(d.quantity)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem' }}>{formatNumber(d.unitCost)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'primary.main' }}>{formatNumber(d.quantity * d.unitCost)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{d.notes || '-'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">{isRTL ? 'لا توجد تفاصيل' : 'No details'}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {transfer.details && transfer.details.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align={isRTL ? 'left' : 'right'} sx={{ fontWeight: 'bold', bgcolor: 'rgba(25,118,210,0.1)', fontSize: '0.85rem' }}>
                      {isRTL ? 'الإجمالي:' : 'Total:'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(25,118,210,0.1)' }}>
                      <Chip label={formatNumber(transfer.details.reduce((s, d) => s + d.quantity, 0))} color="primary" size="small" />
                    </TableCell>
                    <TableCell sx={{ bgcolor: 'rgba(25,118,210,0.1)' }} />
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(25,118,210,0.1)', color: 'primary.main' }}>
                      {formatNumber(transfer.details.reduce((s, d) => s + d.quantity * d.unitCost, 0))}
                    </TableCell>
                    <TableCell sx={{ bgcolor: 'rgba(25,118,210,0.1)' }} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={onClose} size="large">{isRTL ? 'إغلاق' : 'Close'}</Button>
        {canEdit && onEdit && (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={onEdit} size="large" color="primary">
            {isRTL ? 'تعديل' : 'Edit'}
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          size="large"
          sx={{ background: isDarkMode ? 'linear-gradient(45deg, #0d47a1 30%, #1565c0 90%)' : 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)' }}
        >
          {isRTL ? 'طباعة' : 'Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchTransferPreviewDialog;
